<template>
  <span class="share-button">
    <div v-if="isLoaded">
      <a
        href="javascript:void(0)"
        @click="show = true"
        class="btn btn-primary-share"
        data-test="sharing-button"
        >Share</a
      >
      <modal v-model="show" :header="false" :footer="false" style="font-size: 18px">
        <div class="row">
          <div class="col-sm-12">
            <label>
              Allow link sharing? &nbsp;
              <input
                name="allow-sharing"
                type="checkbox"
                class="toggle-switch-checkbox"
                style="vertical-align: bottom; margin-left: 10px"
                v-model="isSharingEnabled"
                data-test="enable-link-sharing-checkbox"
              />
            </label>
            <div style="float: right">
              <button
                @click="close"
                type="button"
                aria-label="Close"
                class="close"
                style="position: relative; z-index: 1060; font-size: 28px; line-height: 1.4"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </div>
        </div>

        <div
          class="row"
          v-if="isSharingEnabled"
          style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e6e6e6"
        >
          <div class="col-sm-9">
            <p class="text-right" style="padding-top: 7px">
              Anyone with this link can view the report:
            </p>
            <div class="invite-link-wrapper form-control form-control-lg hidden">
              <input
                ref="inviteLink"
                type="text"
                :disabled="!isSharingEnabled"
                class="invite-link-input hidden"
                @click="selectCodeUrl()"
                :value="inviteUrl"
                readonly
                data-test="link-sharing-url-textbox"
                aria-label="Link to share report"
              />
            </div>
          </div>
          <div class="col-sm-3 text-right">
            <a
              role="button"
              href="javascript:void(0)"
              @click="copyUrlToClipboard()"
              class="btn btn-lg btn-link"
              style="margin-left: -15px"
              :class="{
                'btn-default-share': !copied,
                'btn-default-tick': copied,
              }"
            >
              {{ copied ? "Copied!" : "Copy link" }}
            </a>
          </div>
        </div>
      </modal>
    </div>
  </span>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import axios from "axios";
import { Modal } from "uiv";
import { Analytics } from "../services/analytics/analytics";
import {
  ReportSharingSettingsApiModel,
  ReportSharingSettingsUpdateApiModel,
  ReportSharingTypeApiModel,
} from "../api-models";

const props = defineProps<{
  reportType: ReportSharingTypeApiModel;
  reportEntityId: string;
  urlDescription: string;
}>();

const isLoaded = ref(false);
const show = ref(false);
const m_isSharingEnabled = ref(false);
const copied = ref(false);
const code = ref("");

const isSharingEnabled = computed({
  get() {
    return m_isSharingEnabled.value;
  },
  set(value: boolean) {
    m_isSharingEnabled.value = value;
    axios.patch("/api/reportsharing", {
      reportType: props.reportType,
      reportEntityId: props.reportEntityId,
      isLinkSharingEnabled: m_isSharingEnabled.value,
    } as ReportSharingSettingsUpdateApiModel);
  },
});

const title = computed(() => `Share '${document.title.replace(" - Gyre", "")}'`);

const close = () => {
  show.value = false;
};

const transformedUrlDescription = computed(() =>
  props.urlDescription
    .toLowerCase()
    .replace(/[^\w ]/gi, "")
    .replace(/[ ]/g, "-"),
);

const inviteUrl = computed(
  () =>
    new URL(`/sharedreport/${code.value}/${transformedUrlDescription.value}`, window.location.href)
      .href,
);

onMounted(() => {
  axios
    .get("/api/reportsharing", {
      params: {
        reportType: props.reportType,
        reportEntityId: props.reportEntityId,
      },
      validateStatus(status) {
        return status >= 200 && status < 300;
      },
    })
    .then((res) => {
      if (res.status === 403 || !res.data) {
        isLoaded.value = false;
        return;
      }
      const model = res.data as ReportSharingSettingsApiModel;
      m_isSharingEnabled.value = model.linkSharing?.isEnabled;
      code.value = model.linkSharing.code;
      isLoaded.value = true;
    })
    .catch(() => {
      isLoaded.value = false; // May not be authorised to change sharing settings
    });
});

function copyUrlToClipboard(): void {
  const tempInput = document.createElement("input");
  tempInput.value = inviteUrl.value;
  document.body.appendChild(tempInput);
  tempInput.select();
  document.execCommand("copy");
  document.body.removeChild(tempInput);
  copied.value = true;
  Analytics.action("Copied Report Share Link", "CopiedReportShareLink", {
    reportType: props.reportType,
    reportEntityId: props.reportEntityId,
  });
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
.share-button {
  display: block;
  min-height: 40px;
}

input h3 {
  margin-top: 50px;
}

.invite-control-tab-transition-enter,
.invite-control-tab-transition-leave-to {
  opacity: 0;
}

.invite-control-fadeable {
  transition: opacity 0.5s;
}

.invite-link-wrapper {
  height: 48px;
  line-height: 32px;
  width: 100% !important;
  background-color: rgba(239, 239, 239, 0.3);
}

.invite-link-wrapper input {
  border-width: 0 !important;
  font-size: 18px;
  outline: none !important;
  width: 100%;
  min-width: 100%;
  background-color: transparent;
  padding: 6px 12px;
  margin: -6px -12px;
}

.invite-code-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;

  margin-bottom: 5px;

  input {
    flex-grow: 1;
    margin-right: 5px;
  }

  > * {
    margin-right: 5px;
  }

  :last-child {
    margin: 0;
  }

  .btn-default .glyphicon {
    position: relative;
    top: 2px;
  }
}
</style>
